import { useState } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { HelpCircle, Download, ExternalLink } from 'lucide-react';
import { docsForModule, openPlaybook, visibleDocs } from '../config/helpDocs';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { Link } from 'react-router-dom';

/**
 * HelpButton — small "?" icon that opens a right-side drawer listing every
 * playbook relevant to the current module. Drop this anywhere in the app; the
 * `module` prop maps to keys defined in /config/helpDocs.js (e.g. "purchase-orders").
 *
 * The drawer also links back to the full Help Center so managers can browse
 * everything if their answer isn't in the module-specific list.
 */
export function HelpButton({ module, label = 'Help', variant = 'outline', size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const perms = useEffectivePermissions();
  const docs = visibleDocs(docsForModule(module), perms);
  if (docs.length === 0) return null;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        data-testid={`help-button-${module}`}
        title="Open the playbook for this module"
      >
        <HelpCircle className="w-4 h-4 mr-1.5" /> {label}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Playbooks for this module</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            {docs.map(d => (
              <div key={d.id} data-testid={`help-drawer-card-${d.id}`} className="border border-slate-200 rounded-lg p-4 bg-surface">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{d.category}</div>
                <h4 className="font-semibold text-slate-900 mb-1.5 leading-snug">{d.title}</h4>
                <p className="text-sm text-slate-600 mb-3">{d.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Updated {d.updated}</span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" title="Open in new tab"
                      onClick={() => openPlaybook(d)}>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" onClick={() => openPlaybook(d, { download: true })}>
                      <Download className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            <Link
              to="/help"
              onClick={() => setOpen(false)}
              className="block text-sm text-blue-700 hover:underline pt-2"
              data-testid="help-drawer-see-all"
            >
              See all playbooks in the Help Center →
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
